import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Promise<Document | null>>} */
  #inFlightPages = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {boolean} */
  #opening = false;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
    this.querySelector('.quick-add__button--choose')?.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
    this.querySelector('.quick-add__button--choose')?.removeEventListener('click', this.handleClick);
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = modalContent.querySelector('variant-picker');
    if (!(variantPicker instanceof VariantPicker)) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Drops gallery chrome so the modal stays a simple variant picker.
   * @param {Element} root
   */
  #simplifyModalContent(root) {
    root
      .querySelectorAll(
        [
          'slideshow-arrows',
          'slideshow-controls',
          'deferred-media',
          'zoom-dialog',
          '.product-media-container__zoom-button',
          '.view-more-details__wrapper',
          'shopify-accelerated-checkout',
          '.shopify-payment-button',
        ].join(', ')
      )
      .forEach((element) => element.remove());

    const title = root.querySelector('.view-product-title');
    const price = root.querySelector('product-price');
    const priceContent = price?.closest('.group-block-content');
    if (title instanceof HTMLElement && priceContent instanceof HTMLElement && !priceContent.contains(title)) {
      priceContent.prepend(title);
    }
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.#openQuickAddModal();

    if (this.#opening) return;
    this.#opening = true;

    try {
      const currentUrl = this.productPageUrl;
      if (!currentUrl) return;

      const productGrid = await this.#getProductGrid(currentUrl);
      if (!productGrid) return;

      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      this.#simplifyModalContent(freshContent);
      // morph() moves every node the modal doesn't have yet out of
      // freshContent and into the modal, so after the first morph
      // freshContent is missing those nodes. The variant picker re-render
      // needs its own untouched copy, otherwise it strips the option inputs
      // and labels from the modal (empty black buttons, nothing selectable).
      const variantPickerSource = /** @type {Element} */ (freshContent.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(variantPickerSource);
    } finally {
      this.#opening = false;
    }
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!dialogComponent || typeof dialogComponent.showDialog !== 'function') return;

    this.#stayVisibleUntilDialogCloses(/** @type {QuickAddDialog} */ (dialogComponent));
    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Returns cached product grid content, fetching it when needed.
   * @param {string} productPageUrl
   * @returns {Promise<Element | null>}
   */
  async #getProductGrid(productPageUrl) {
    const cached = this.#cachedContent.get(productPageUrl);
    if (cached) return cached;

    const html = await this.fetchProductPage(productPageUrl);
    if (!html) return this.#cachedContent.get(productPageUrl) ?? null;

    const gridElement =
      html.querySelector('[data-product-grid-content]') ||
      html.querySelector('.product-information__grid') ||
      html.querySelector('.product-details');
    if (!gridElement) return null;

    const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
    this.#cachedContent.set(productPageUrl, productGrid);
    return productGrid;
  }

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    const inFlight = this.#inFlightPages.get(productPageUrl);
    if (inFlight) return inFlight;

    const request = this.#loadProductPage(productPageUrl);
    this.#inFlightPages.set(productPageUrl, request);

    try {
      return await request;
    } finally {
      this.#inFlightPages.delete(productPageUrl);
    }
  }

  /**
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async #loadProductPage(productPageUrl) {
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const gridElement =
        html.querySelector('[data-product-grid-content]') ||
        html.querySelector('.product-information__grid') ||
        html.querySelector('.product-details');
      if (gridElement && !this.#cachedContent.has(productPageUrl)) {
        this.#cachedContent.set(productPageUrl, /** @type {Element} */ (gridElement.cloneNode(true)));
      }

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
